/**
 * User Journey Page
 *
 * Search for a buyer by email, anonymous_id, or product, then view
 * their complete page-by-page timeline with attribution data.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { trafficAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import JourneyAttributionModels from '../components/JourneyAttributionModels'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  Search, Loader2, AlertCircle, AlertTriangle, ArrowLeft, ExternalLink,
  Mail, CreditCard, ShoppingCart, Eye, MousePointer
} from 'lucide-react'

function formatTime(ts) {
  if (!ts) return '-'
  const d = new Date(ts.value || ts)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' })
}

function formatDate(ts) {
  if (!ts) return '-'
  const d = new Date(ts.value || ts)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function timeBetween(a, b) {
  if (!a || !b) return null
  const msA = new Date(a.value || a).getTime()
  const msB = new Date(b.value || b).getTime()
  const diff = msB - msA
  if (diff < 0) return null
  if (diff < 60000) return `${Math.round(diff / 1000)}s`
  if (diff < 3600000) return `${Math.round(diff / 60000)}m`
  if (diff < 86400000) return `${(diff / 3600000).toFixed(1)}h`
  return `${(diff / 86400000).toFixed(1)}d`
}

const EVENT_CONFIG = {
  page_view: { icon: Eye, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Page View' },
  email_entered: { icon: Mail, color: 'text-blue-600', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Email Entered' },
  plan_selected: { icon: ShoppingCart, color: 'text-purple-600', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Plan Selected' },
  payment_succeeded: { icon: CreditCard, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Payment' },
}

export default function UserJourneyPage() {
  const { currentClinicId } = useAuthStore()
  const requestVersion = useRef(0)
  const [searchInput, setSearchInput] = useState('')
  const [lookupResults, setLookupResults] = useState(null)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [journey, setJourney] = useState(null)
  const [journeyLoading, setJourneyLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedEvent, setExpandedEvent] = useState(null)
  const [mismatches, setMismatches] = useState(null)
  const [mismatchLoading, setMismatchLoading] = useState(false)

  // Split from the submit handler so a deeplink can run the same search: the
  // New Customers list links here by EMAIL, because the funnel can hand one
  // buyer two anonymous ids and the lookup is what surfaces both.
  const runSearch = useCallback(async (term) => {
    const q = (term || '').trim()
    if (!q) return
    const version = ++requestVersion.current
    try {
      setLookupLoading(true)
      setError(null)
      setJourney(null)
      setLookupResults(null)

      // If it looks like an anonymous_id, go straight to journey
      if (q.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/i) || q.length > 30) {
        await loadJourney(q)
      } else {
        const res = await trafficAPI.getUserJourneyLookup({ q })
        if (version === requestVersion.current && res.data.success) {
          setLookupResults(res.data.data.results)
        }
      }
    } catch (err) {
      console.error('[USER-JOURNEY] Error:', err)
      if (version === requestVersion.current) setError(err.response?.data?.error?.message || err.message)
    } finally {
      if (version === requestVersion.current) setLookupLoading(false)
    }
  }, [])

  const handleSearch = useCallback((e) => {
    e?.preventDefault()
    return runSearch(searchInput)
  }, [runSearch, searchInput])

  const loadJourney = useCallback(async (anonymousId) => {
    const version = ++requestVersion.current
    setJourney(null)
    try {
      setJourneyLoading(true)
      setError(null)
      const res = await trafficAPI.getUserJourney(anonymousId)
      if (version === requestVersion.current && res.data.success) {
        setJourney(res.data.data)
        setLookupResults(null)
      }
    } catch (err) {
      console.error('[USER-JOURNEY] Error:', err)
      if (version === requestVersion.current) setError(err.response?.data?.error?.message || err.message)
    } finally {
      if (version === requestVersion.current) {
        setJourneyLoading(false)
        setLookupLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    requestVersion.current += 1
    setJourney(null)
    setLookupResults(null)
    setError(null)
    setJourneyLoading(false)
    setLookupLoading(false)
    return () => { requestVersion.current += 1 }
  }, [currentClinicId])

  // Auto-load from the URL: `id` is an anonymous_id and goes straight to the
  // journey (Conversion Paths links that way); `q` is anything the lookup takes
  // — an email, a product — and runs the search instead (New Customers links
  // that way, since it knows the customer by email and not by id).
  const [searchParams] = useSearchParams()
  useEffect(() => {
    const id = searchParams.get('id')
    const q = searchParams.get('q')
    if (id) {
      setSearchInput(id)
      loadJourney(id)
    } else if (q) {
      setSearchInput(q)
      runSearch(q)
    }
    // Only on mount - subsequent searches are user-driven
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loading = lookupLoading || journeyLoading

  // Detect sessions from the timeline
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
  const sessions = useMemo(() => {
    if (!journey?.timeline) return []
    const sessions = []
    let currentSession = null

    for (let i = 0; i < journey.timeline.length; i++) {
      const event = journey.timeline[i]
      const ts = new Date(event.timestamp?.value || event.timestamp).getTime()
      const isPageView = event.event_type === 'page_view'

      let isNewSession = false
      if (!currentSession) {
        isNewSession = true
      } else if (isPageView) {
        const gap = ts - currentSession.lastTs
        // New session if: >30min gap, new click ID, or new UTM source
        const hasNewClickId = !!(event.fbclid || event.gclid || event.ttclid) &&
          (event.fbclid !== currentSession.lastClickId)
        const hasNewUtm = event.utm_source && event.utm_source !== currentSession.utmSource

        if (gap > SESSION_TIMEOUT_MS || hasNewClickId || hasNewUtm) {
          isNewSession = true
        }
      }

      if (isNewSession && isPageView) {
        const source = event.utm_source || event.context_campaign_source ||
          (event.fbclid ? 'meta' : event.gclid ? 'google' : event.ttclid ? 'tiktok' : event.affid ? 'everflow' : 'direct')
        currentSession = {
          index: sessions.length,
          startIdx: i,
          startTs: ts,
          lastTs: ts,
          source,
          utmSource: event.utm_source,
          utmCampaign: event.utm_campaign || event.context_campaign_name,
          landingPage: event.context_page_path || event.page_path || '/',
          lastClickId: event.fbclid || event.gclid || event.ttclid || null,
          events: 0,
        }
        sessions.push(currentSession)
      }

      if (currentSession) {
        currentSession.lastTs = ts
        currentSession.events++
      }

      // Tag event with session index
      event._sessionIdx = currentSession?.index ?? -1
    }

    return sessions
  }, [journey])

  // Build a set of event indices that start a new session (for rendering dividers)
  const sessionStartIndices = useMemo(() => {
    return new Set(sessions.map(s => s.startIdx))
  }, [sessions])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">User Journey</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Trace a buyer's complete page journey and attribution from Segment data
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="py-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by email, anonymous_id, or product name..."
                className="w-full pl-10 pr-4 py-2 text-sm border rounded-md bg-background"
              />
            </div>
            <Button type="submit" disabled={loading || !searchInput.trim()}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive"><CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="w-5 h-5" /><span>{error}</span>
          </div>
        </CardContent></Card>
      )}

      {/* Mismatched Conversions */}
      {!journey && !lookupResults && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Mismatched Conversions</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Buyers whose ad campaign doesn't match the product they purchased
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    setMismatchLoading(true)
                    const res = await trafficAPI.getUserJourneyMismatches({ days: 30 })
                    if (res.data.success) setMismatches(res.data.data)
                  } catch (err) {
                    setError(err.response?.data?.error?.message || err.message)
                  } finally {
                    setMismatchLoading(false)
                  }
                }}
                disabled={mismatchLoading}
              >
                {mismatchLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                {mismatches ? 'Refresh' : 'Find Mismatches'}
              </Button>
            </div>
          </CardHeader>
          {mismatches && (
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">
                {mismatches.total_mismatches} mismatched out of {mismatches.total_conversions} conversions (last {mismatches.days} days)
              </p>
              {mismatches.mismatches.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No mismatches found</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Email</th>
                        <th className="text-left p-3 font-medium">Campaign</th>
                        <th className="text-left p-3 font-medium">Campaign Lane</th>
                        <th className="text-left p-3 font-medium">Product</th>
                        <th className="text-left p-3 font-medium">Product Lane</th>
                        <th className="text-right p-3 font-medium">Amount</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {mismatches.mismatches.map((m, i) => (
                        <tr key={i} className="border-t hover:bg-muted/30 bg-yellow-50/30 dark:bg-yellow-950/10">
                          <td className="p-3 text-xs">{formatDate(m.payment_at)}</td>
                          <td className="p-3 text-sm">{m.email || '-'}</td>
                          <td className="p-3 text-xs break-words max-w-[200px]">{m.campaign}</td>
                          <td className="p-3">
                            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-muted">{m.campaign_lane_name}</span>
                          </td>
                          <td className="p-3 font-mono text-xs">{m.product}</td>
                          <td className="p-3">
                            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-muted">{m.product_lane_name}</span>
                          </td>
                          <td className="p-3 text-right font-mono">{m.amount != null ? `$${m.amount}` : '-'}</td>
                          <td className="p-3">
                            <Button
                              size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => loadJourney(m.anonymous_id)}
                              disabled={journeyLoading}
                            >
                              <MousePointer className="h-3 w-3 mr-1" /> Journey
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Lookup Results */}
      {lookupResults && !journey && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Payment Events ({lookupResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {lookupResults.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No matching payment events found in the last 90 days</p>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Date</th>
                      <th className="text-left p-3 font-medium">Email</th>
                      <th className="text-left p-3 font-medium">Product</th>
                      <th className="text-right p-3 font-medium">Amount</th>
                      <th className="text-left p-3 font-medium">Anonymous ID</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookupResults.map((r, i) => (
                      <tr key={i} className="border-t hover:bg-muted/30">
                        <td className="p-3 text-xs">{formatDate(r.timestamp)}</td>
                        <td className="p-3 text-sm">{r.email || '-'}</td>
                        <td className="p-3 font-mono text-xs">{r.product || '-'}</td>
                        <td className="p-3 text-right font-mono">{r.amount != null ? `$${r.amount}` : '-'}</td>
                        <td className="p-3 font-mono text-xs text-muted-foreground">{r.anonymous_id}</td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => loadJourney(r.anonymous_id)}
                            disabled={journeyLoading}
                          >
                            <MousePointer className="h-3 w-3 mr-1" />
                            View Journey
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Journey Timeline */}
      {journey && (
        <>
          {lookupResults === null && (
            <Button variant="ghost" size="sm" onClick={() => { requestVersion.current += 1; setJourney(null); setLookupResults(null) }} className="gap-1">
              <ArrowLeft className="h-4 w-4" /> New search
            </Button>
          )}

          {/* Attribution Card */}
          {journey.attribution && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">First recorded Segment visit</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">Source</div>
                    <div className="font-medium">{journey.attribution.source}</div>
                  </div>
                  {journey.attribution.utm_campaign && (
                    <div>
                      <div className="text-xs text-muted-foreground">Campaign</div>
                      <div className="font-medium break-words">{journey.attribution.utm_campaign}</div>
                    </div>
                  )}
                  {journey.attribution.utm_content && (
                    <div>
                      <div className="text-xs text-muted-foreground">Content / Ad</div>
                      <div className="font-medium break-words">{journey.attribution.utm_content}</div>
                    </div>
                  )}
                  {journey.attribution.utm_medium && (
                    <div>
                      <div className="text-xs text-muted-foreground">Medium</div>
                      <div className="font-medium">{journey.attribution.utm_medium}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-muted-foreground">Landing Page</div>
                    <div className="font-mono text-xs break-all">{journey.attribution.landing_page}</div>
                  </div>
                  {journey.attribution.referrer && journey.attribution.referrer !== '' && (
                    <div>
                      <div className="text-xs text-muted-foreground">Referrer</div>
                      <div className="font-mono text-xs break-all">{journey.attribution.referrer}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-muted-foreground">First Seen</div>
                    <div className="font-medium">{formatTime(journey.attribution.first_seen)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <JourneyAttributionModels key={`${currentClinicId}:${journey.anonymous_id}`} journey={journey} />

          {/* Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Sessions</span>
              <p className="text-xl font-bold">{sessions.length}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Page Views</span>
              <p className="text-xl font-bold">{journey.summary.page_views}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Unique Pages</span>
              <p className="text-xl font-bold">{journey.summary.unique_pages}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Time Span</span>
              <p className="text-xl font-bold">{timeBetween(journey.summary.first_seen, journey.summary.last_seen) || '-'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <span className="text-xs text-muted-foreground">Funnel</span>
              <div className="flex items-center gap-1 mt-1">
                {journey.summary.has_email && <Mail className="h-4 w-4 text-blue-600" title="Email entered" />}
                {journey.summary.has_plan && <ShoppingCart className="h-4 w-4 text-purple-600" title="Plan selected" />}
                {journey.summary.has_payment && <CreditCard className="h-4 w-4 text-green-600" title="Payment" />}
                {!journey.summary.has_email && !journey.summary.has_plan && !journey.summary.has_payment && (
                  <span className="text-xs text-muted-foreground">Browse only</span>
                )}
              </div>
            </CardContent></Card>
          </div>

          {/* Timeline */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                Timeline ({journey.timeline.length} events)
                <span className="text-xs text-muted-foreground font-normal ml-2">
                  {journey.anonymous_id}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

                <div className="space-y-0">
                  {journey.timeline.map((event, i) => {
                    const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.page_view
                    const Icon = config.icon
                    const prevEvent = i > 0 ? journey.timeline[i - 1] : null
                    const gap = prevEvent ? timeBetween(prevEvent.timestamp, event.timestamp) : null
                    const isConversion = event.event_type !== 'page_view'

                    const sessionStart = sessionStartIndices.has(i)
                    const session = sessionStart ? sessions.find(s => s.startIdx === i) : null

                    return (
                      <div key={i}>
                        {/* Session divider */}
                        {sessionStart && session && (
                          <div className={`flex items-center gap-2 ${session.index > 0 ? 'mt-4 pt-3 border-t-2 border-primary/20' : 'mb-1'}`}>
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center z-10">
                              <span className="text-[10px] font-bold text-primary">{session.index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <span className="text-xs font-semibold text-primary">Session {session.index + 1}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {session.source}
                                {session.utmCampaign ? ` / ${session.utmCampaign}` : ''}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2">
                                — {session.events} events, {timeBetween(session.startTs, session.lastTs) || '<1s'}
                              </span>
                              {session.index > 0 && prevEvent && (
                                <span className="text-xs text-yellow-600 ml-2">
                                  ({gap} since last session)
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Time gap indicator (only within session, not at session boundaries) */}
                        {!sessionStart && gap && gap !== '0s' && (
                          <div className="flex items-center gap-2 pl-3 py-0.5">
                            <div className="w-5 flex justify-center">
                              <span className="text-[9px] text-muted-foreground/50">{gap}</span>
                            </div>
                          </div>
                        )}

                        <div
                          className={`flex items-start gap-3 py-1.5 px-2 rounded cursor-pointer ${isConversion ? config.bg : 'hover:bg-muted/30'} ${expandedEvent === i ? 'ring-1 ring-primary/30' : ''}`}
                          onClick={() => setExpandedEvent(expandedEvent === i ? null : i)}
                        >
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center z-10 ${isConversion ? config.bg : 'bg-background border'}`}>
                            <Icon className={`h-3 w-3 ${config.color}`} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs text-muted-foreground">{formatTime(event.timestamp)}</span>
                              <span className="text-xs text-muted-foreground/50">{event.dataset}</span>
                            </div>

                            {event.event_type === 'page_view' ? (
                              <div className="text-sm font-mono break-all">
                                {event.context_page_path || event.page_path || '/'}
                                {(event.utm_source || event.context_campaign_source) && (
                                  <span className="ml-2 text-xs text-muted-foreground">
                                    via {event.utm_source || event.context_campaign_source}
                                    {(event.utm_campaign || event.context_campaign_name) ? ` / ${event.utm_campaign || event.context_campaign_name}` : ''}
                                  </span>
                                )}
                                {event.device_type && (
                                  <span className="ml-2 text-[10px] text-muted-foreground/70">{event.device_type}</span>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm font-medium">
                                <span className={config.color}>{config.label}</span>
                                {event.event_detail && (
                                  <span className="ml-2 font-mono text-xs">{event.event_detail}</span>
                                )}
                                {event.amount != null && (
                                  <span className="ml-2 font-mono">${event.amount}</span>
                                )}
                              </div>
                            )}

                            {/* Expanded detail */}
                            {expandedEvent === i && (
                              <EventDetail event={event} />
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function parseQueryParams(qs) {
  if (!qs) return {}
  const params = {}
  const str = qs.startsWith('?') ? qs.slice(1) : qs
  for (const pair of str.split('&')) {
    const [key, val] = pair.split('=')
    if (key) {
      try { params[decodeURIComponent(key)] = decodeURIComponent(val || '') }
      catch { params[key] = val || '' }
    }
  }
  return params
}

function EventDetail({ event }) {
  // Show ALL non-empty fields from the event object
  const SKIP = new Set(['event_type', 'timestamp', 'dataset'])
  const fields = []

  for (const [key, val] of Object.entries(event)) {
    if (SKIP.has(key)) continue
    if (val == null || val === '' || val === false) continue
    // Format BigQuery timestamp objects
    const display = typeof val === 'object' && val.value ? String(val.value) : String(val)
    if (!display || display === 'null') continue
    fields.push([key, display])
  }

  if (fields.length === 0) {
    return <div className="mt-2 text-xs text-muted-foreground">No additional details</div>
  }

  // Sort: put important fields first
  const priority = ['context_page_url', 'context_page_path', 'context_page_title', 'context_page_referrer', 'context_page_search',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'device_type', 'context_ip', 'context_user_agent',
    'context_locale', 'context_timezone', 'event_detail', 'amount', 'email', 'fbclid', 'gclid', 'ttclid', 'affid']
  const prioritySet = new Set(priority)
  fields.sort((a, b) => {
    const ai = priority.indexOf(a[0])
    const bi = priority.indexOf(b[0])
    if (ai >= 0 && bi >= 0) return ai - bi
    if (ai >= 0) return -1
    if (bi >= 0) return 1
    return a[0].localeCompare(b[0])
  })

  return (
    <div className="mt-2 border rounded-md bg-background/50 p-2" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-1 gap-y-0.5">
        {fields.map(([label, value], i) => (
          <div key={i} className="flex gap-2 text-xs min-w-0">
            <span className="text-muted-foreground flex-shrink-0 w-40 text-right font-mono">{label}</span>
            <span className="font-mono break-all">{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
